<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import type { FieldSchema } from '../../../types';
import { capitalizeStr } from '@Helpers';

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  field?: FieldSchema;
  placeholder?: string;
  preDefinedColors?: boolean;
}>();

const model = defineModel();
const colorModel = ref();
const preDefinedColorsModel = ref();

onMounted(() => initModel());

const preDefinedColorsOptions = computed(() => [
  { hex: '#F5F7F8', color: 'color.white', value: 'white' },
  { hex: '#E33347', color: 'color.darkRed', value: 'dark-red' },
  { hex: '#FB5831', color: 'color.darkOrange', value: 'dark-orange' },
  { hex: '#FC8F27', color: 'color.lightOrange', value: 'light-orange' },
  { hex: '#EBBB2D', color: 'color.yellow', value: 'yellow' },
  { hex: '#9BC83C', color: 'color.liteGreen', value: 'lite-green' },
  { hex: '#33BDA2', color: 'color.green', value: 'green' },
  { hex: '#5ACB6A', color: 'color.mediumGreen', value: 'medium-green' },
  { hex: '#20A0E3', color: 'color.lightBlue', value: 'light-blue' },
  { hex: '#3B7BD7', color: 'color.blue', value: 'blue' },
  { hex: '#6E64E8', color: 'color.mediumBlue', value: 'medium-blue' },
  { hex: '#9F58DA', color: 'color.purple', value: 'purple' },
  { hex: '#DE59DA', color: 'color.hotPink', value: 'hot-pink' },
  { hex: '#E64590', color: 'color.pink', value: 'pink' },
  { hex: '#FA87A3', color: 'color.lightPink', value: 'light-pink' },
  { hex: '#82999C', color: 'color.grey', value: 'grey' },
]);

function initModel() {
  if (!model.value)
    return props.field?.settings?.preDefinedColors || props.preDefinedColors
      ? (model.value = null)
      : (model.value = '#FF0101');

  props.field?.settings?.preDefinedColors || props.preDefinedColors
    ? (preDefinedColorsModel.value = model.value)
    : (colorModel.value = model.value);
}

function updatePreDefinedColorModel(color: any) {
  preDefinedColorsModel.value?.hex === color.hex
    ? (preDefinedColorsModel.value = null)
    : (preDefinedColorsModel.value = color);

  onModelUpdate();
}

function onModelUpdate() {
  if (props.field?.settings?.preDefinedColors || props.preDefinedColors)
    return (model.value = preDefinedColorsModel.value);

  model.value = `#${colorModel.value}`;
}
</script>

<template>
  <div
    v-if="field?.settings?.preDefinedColors || preDefinedColors"
    class="flex flex-wrap gap-2"
  >
    <div
      v-for="color in preDefinedColorsOptions"
      v-tooltip.bottom="capitalizeStr($t(color.color))"
      :class="[
        'size-5 cursor-pointer rounded-full border',
        preDefinedColorsModel?.hex === color.hex ? 'border-2 border-gray-700' : 'border-gray-300',
      ]"
      :key="color.hex"
      :style="{ backgroundColor: color.hex }"
      @click="updatePreDefinedColorModel(color)"
    ></div>
  </div>

  <div
    v-else
    class="flex gap-2"
  >
    <ColorPicker
      v-model="colorModel"
      format="hex"
      inputId="cp-hex"
      @change="onModelUpdate"
    />

    <p
      class="text-sm text-red-500"
      :style="{ color: colorModel?.startsWith('#') ? colorModel : '#' + colorModel }"
    >
      {{ $t('components.forms.notePreview') }}
    </p>
  </div>
</template>
